import type { ApproximateMonth, CalendarDate, IsoTimestamp } from '~/types/dates'
import type { PhaseType } from './phase'
import type { DesiredOutcome } from './project'
import type { Confidence, EvidenceItem } from './rulesOpinion'

/**
 * What the route engine produces (D-062, D-071).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A ROUTE IS A RECOMMENDATION, NOT AN ACTION.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Producing one starts nothing. The user confirms it and the project is created
 * by that explicit act (D-020). Nothing in the codebase may create a phase, a
 * goal or a project from a value of this type on its own.
 *
 * As with the weekly opinion, nothing here is formatted for display and nothing
 * is rounded: rounding happens at serialization (D-039) and formatting at the
 * presentation boundary (D-041).
 */

/**
 * Which side of the two approved waist-to-height boundaries the user is on
 * (D-071).
 *
 * Named bands rather than a raw comparison at each call site, because the same
 * three-way split drives every row of the mapping and a fourth reading of
 * `>= 0.5` written by hand is a fourth chance to get the boundary wrong.
 *
 * **These are routing boundaries, not health verdicts.** The published band is
 * a cardiometabolic screening threshold repurposed once, deliberately; no
 * output may state or imply a risk category (D-015, D-071).
 */
export type ConditionBand = 'fatLossPriority' | 'middle' | 'leanEnough'

export interface CurrentCondition {
  readonly waistToHeight: number
  readonly band: ConditionBand
  readonly waistCm: number
  readonly heightCm: number
  /**
   * The Navy circumference estimate, when it could be computed.
   *
   * Absent does **not** block a route: waist-to-height is the primary evidence
   * and needs no neck measurement, so an invalid estimate is reported as
   * missing evidence rather than a reason to say nothing (D-071).
   */
  readonly estimatedBodyFatPercent: number | undefined
}

/**
 * Why the recommended route is not the one the stated outcome names.
 *
 * D-071: "Two redirects are what make this a route engine rather than a menu,
 * and both must state their reasoning." Carried as a distinct field rather than
 * buried in the reasoning list, so the interface can show plainly that Project
 * Sola disagreed with the obvious reading and why — a redirect the user does
 * not notice is a menu that silently picked for them.
 */
export interface RouteRedirect {
  /** The phase the outcome would have implied on its own. */
  readonly insteadOf: PhaseType
  readonly because: string
}

/**
 * Something Project Sola does not plan, named rather than omitted (D-062).
 *
 * The premise of the whole feature is the four-way question, and one of those
 * four routes is deferred entirely (D-012, F-001). Saying nothing would leave
 * the user wondering why the option they have heard of is missing.
 */
export interface NotPlanned {
  readonly capability: string
  readonly reason: string
}

/**
 * How long the project runs.
 *
 * **This is the user's own horizon restated, not a projection.** They gave a
 * target date; this is the arithmetic from the start date to it, expressed to
 * the month per D-068 because anything the app says about the future is said to
 * the month. It forecasts nothing and implies no appearance outcome — a genuine
 * projection needs a target weight and belongs to `calculations/projection.ts`,
 * which `rules/` does not import.
 */
export interface RouteDuration {
  readonly weeks: number
  readonly approximateMonth: ApproximateMonth
}

export interface SuggestedProject {
  /** A starting name the user edits. Never imposed. */
  readonly name: string
  readonly startDate: CalendarDate
  readonly targetDate: CalendarDate
}

export interface RouteRecommendation {
  readonly outcome: 'route'
  readonly recommendedPhase: PhaseType
  readonly desiredOutcome: DesiredOutcome
  readonly condition: CurrentCondition
  /** Why this route, in the engine's own terms. Never empty. */
  readonly reasoning: readonly string[]
  readonly redirect: RouteRedirect | undefined
  readonly suggestedProject: SuggestedProject
  readonly duration: RouteDuration
  /** What the phase is for, in one sentence. */
  readonly primaryObjective: string
  /**
   * The phase's suggested weekly weight change, signed, percent of body weight.
   *
   * Read from the approved D-065 defaults, never invented here. D-062 forbade
   * the engine from inventing a target range when four of the five phases had
   * none; **B-004 closed that gap** (D-065), so the engine now reads an approved
   * value rather than refusing. The phase record still stores its own copy and
   * the weekly engine still judges against that, not against this.
   */
  readonly weeklyTargetPercent: { readonly min: number; readonly max: number }
  /**
   * What the project is aiming at, stated up front.
   *
   * **An aim, never a verdict** (D-071). The completion summary reports counts
   * and coverage and may never grade anything against these, because a met /
   * not-met result is a score in all but name and D-062 forbids one.
   */
  readonly successCriteria: readonly string[]
  readonly confidence: Confidence
  readonly confidenceReason: string
  readonly evidence: readonly EvidenceItem[]
  readonly missingEvidence: readonly string[]
  readonly notPlanned: readonly NotPlanned[]
  readonly ruleId: string
  readonly engineVersion: string
  readonly evaluatedAt: IsoTimestamp
}

/**
 * The engine has nothing to say, and says so (D-071).
 *
 * Waist-to-height needs a valid waist median and a height. Without them there
 * is no route and no confidence, and this **is never exported or stored as a
 * recommendation** — the same rule that governs `PhaseRulesUnavailable`.
 */
export interface RouteUnavailable {
  readonly outcome: 'routeUnavailable'
  readonly reason: 'noWaistToHeight' | 'unknownOutcome'
  readonly missingEvidence: readonly string[]
  readonly engineVersion: string
  readonly evaluatedAt: IsoTimestamp
}

export type RouteOutcome = RouteRecommendation | RouteUnavailable

export function isRouteRecommendation(outcome: RouteOutcome): outcome is RouteRecommendation {
  return outcome.outcome === 'route'
}
